import React, { createContext, useEffect, useState } from "react";
import { ActionableEvent, ActionableEvents } from "@eave/shared/types/api/actionable-events";
import { ClosingSummaryUpdateRequest } from "@eave/shared/types/api";
import APIClient from "../../../APIClient";
import Spinner from "../../../shared/Spinner";

interface ActionableEventContext {
  busy: boolean;
  closingDate: string;
  actionableEvents: ActionableEvent[];
  ackActionableEvent(eventID: number, shouldSendToHL: boolean): Promise<any>;
  reload(): void;
  updateLoanApp(
    newClosingDate: string,
    closingDateChangeReason: string,
    closingDateChangeCategory: string,
  ): Promise<any>;
}

export const ActionableEventContext = createContext<ActionableEventContext>({
  busy: false,
  closingDate: null,
  actionableEvents: [],
  ackActionableEvent: () => {
    return Promise.resolve();
  },
  reload: () => {},
  updateLoanApp: () => {
    return Promise.resolve();
  },
});

const ActionableEventProvider = ({ appId, closingDate, children, reloadApp }) => {
  const [loading, setLoading] = useState<boolean>(true); // spinner for whole-page loads/reloads
  const [busy, setBusy] = useState<boolean>(false); // UI action lock while API request in flight

  const [actionableEvents, setActionableEvents] = useState<ActionableEvent[] | null>(null);

  const loadActionableEvents = () =>
    APIClient.get(`/uw/loan_applications/${appId}/actionable_event`).then((result: any) =>
      setActionableEvents(
        result.actionable_events.filter(event => {
          return [
            ActionableEvents.CashOfferPurchaseCOEDateChangeEvent,
            ActionableEvents.CashOfferSellCOEDateChangeEvent,
            ActionableEvents.TradeInCOEDateChangeEvent,
          ].includes(event.name);
        }),
      ),
    );

  const updateLoanApplication = (
    newClosingDate: string,
    closingDateChangeReason: string,
    closingDateChangeCategory: string,
  ): Promise<any> => {
    const request: ClosingSummaryUpdateRequest = {};
    if (closingDate !== newClosingDate) {
      if (closingDateChangeReason === "" || closingDateChangeCategory === "") {
        return Promise.reject();
      }
      request.closing_date_change_reason = closingDateChangeReason;
      request.closing_date_change_category = closingDateChangeCategory;
      request.closing_date = newClosingDate;
    }
    setBusy(true);
    return APIClient.put(`/loan_applications/${appId}`, request).finally(() => {
      reloadApp(appId);
      setBusy(false);
    });
  };

  const ack = (actionableEventID: number, shouldSendToHL: boolean): Promise<any> => {
    setBusy(true);
    return APIClient.patch(`/uw/actionable_event/${actionableEventID}`, {
      send_ack_to_homelight: shouldSendToHL,
    }).finally(() => {
      setBusy(false);
    });
  };

  useEffect(() => {
    setLoading(true);
    loadActionableEvents().finally(() => {
      setLoading(false);
    });
  }, [appId]);

  if (loading) {
    return <Spinner active />;
  }

  return (
    <ActionableEventContext.Provider
      value={{
        busy,
        closingDate,
        actionableEvents,
        ackActionableEvent: ack,
        reload: loadActionableEvents,
        updateLoanApp: updateLoanApplication,
      }}
    >
      {children}
    </ActionableEventContext.Provider>
  );
};

export default ActionableEventProvider;
